import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, of, Subject } from 'rxjs';
import { TaskService } from '../../../task-management/services/task.service';
import { map, take } from 'rxjs/operators';
import { INavTab } from '../../models/nav-tab.interface';
import { Router } from '@angular/router';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { Select, Store } from '@ngxs/store';
import { TaskManagementState } from 'app/store/task-management/task-management.state';
import { GetUserActionItemTypes } from 'ng2-app/modules/today/state/user-action-items.action';
import { GetTodayBadgeCounts } from 'app/modules/today/state/today-badge-counts/today-badge-counts.action';
import { TodayBadgeCountsState } from 'app/modules/today/state/today-badge-counts/today-badge-counts.state';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {

  @Select(TaskManagementState.inboxCount) inboxBadgeCount$: Observable<number>;
  @Select(TodayBadgeCountsState.total) todayBadgeCount$: Observable<number>;

  @Input() user: LenderUser;

  pipelineTabs: INavTab[] = [
    {
      key: 'today',
      text: 'Today',
      uri: '/today',
      permission: 'canViewTodayTab',
    },
    {
      key: 'tasks',
      text: 'Inbox',
      uri: '/',
      uriRegex: '^/$'
    },
    {
      key: 'businesses',
      text: 'Businesses',
      uri: '/list',
    },
    {
      key: 'funnel',
      text: 'Funnel',
      uri: '/funnel',
    },
    {
      key: 'deals',
      text: 'Deals',
      uri: '/deals',
      permission: 'viewTeamDeals',
    },
    {
      key: 'performance',
      text: 'Performance',
      uri: '/performance',
      permission: 'featureNewPerformance',
    },
    {
      key: 'performance-cx',
      text: 'CS Performance',
      uri: '/cx-performance',
      permission: 'viewCXPerformanceTab',
    },
    {
      key: 'bulk-submission',
      text: 'Bulk Submission',
      uri: '/admin/bulk-submission',
      permission: 'canBulkSubmitBorrowersViaLenderApi',
    },
  ];

  visibleTabs$: Observable<INavTab[]>;
  activeTab: INavTab|null;
  destroyed$ = new Subject<void>();
  menuOpen = false;

  constructor(
    private location: Location,
    private taskService: TaskService,
    private router: Router,
    private store: Store,
  ) {}

  ngOnInit() {

    this.visibleTabs$ = of(this.pipelineTabs.filter(this.tabVisibleToUser(this.user)));

    this.taskService.get({
      taskType: 'myTasks',
      callType: 'all',
      sortBy: {
        sortCol: 'created',
        sortDirection: 'desc'
      },
    }).pipe(take(1)).subscribe();

    // When the route changes, update the active tab accordingly
    this.location.onUrlChange(this.activateTabFromLocation.bind(this));

    // Use the current location to activate the current tab
    this.activateTabFromLocation();

    this.store.dispatch(new GetTodayBadgeCounts(this.user?.id));
    this.store.dispatch(new GetUserActionItemTypes());
  }

  ngOnDestroy() {
      this.destroyed$.next();
  }

  tabs() {
      return this.pipelineTabs;
  }

  onTabClick(tab: INavTab) {
    this.router.navigate([tab.uri]);
  }

  activateTab(tabToActivate: INavTab|undefined) {
      if (!tabToActivate || !this.pipelineTabs.some(tab => tab === tabToActivate)) {
          this.deactivateTabs();
      }
      this.visibleTabs$.pipe(take(1)).subscribe(() => {
          if (!tabToActivate) {
              return;
          }
          this.activeTab = tabToActivate;
      });
  }

  deactivateTabs() {
      this.activeTab = null;
  }

  route(tab: string) {
    console.log(tab);
  }

  displayBadgeCount(count) {
    if (count === 0) {
      return 0;
    }
    if (!count) {
      return null;
    }
    if (count > 100) {
      return 99;
    }
    if (count > 0) {
      return count;
    }
  }


  private activateTabFromLocation(path = '') {
      path ||= this.location.path();
      if (path.charAt(0) !== '/') {
          path = '/' + path;
      }
      this.tabFromPath(path).pipe(take(1)).subscribe(this.activateTab.bind(this));
  }

  private tabFromPath(path: string): Observable<INavTab|undefined> {
      return this.visibleTabs$.pipe(map((tabs) => tabs
              .find((tab) => {
                  const regexString = tab.uriRegex ? tab.uriRegex : tab.uri;
                  const regex = new RegExp(`^${regexString}`, 'g');
                  return regex.test(path);
              })));
  }

  private tabWithKey(key: string): Observable<INavTab|undefined> {
      return this.visibleTabs$.pipe(
          map((tabs) => tabs.find(tab => tab?.key === key))
      );
  }

  private tabVisibleToUser(user: LenderUser): (tab: INavTab) => boolean {
      return (tab) => {
          if (tab.disableViewFor) {
              if (this.user.permissions.includes(tab.disableViewFor)) {
                  return false;
              }
          }

          // HIDE AND SNEAK FOR GLOBAL ACTIVITIES
          if (!user?.permissions?.includes('showActivitesTab') && tab.key === 'activity') {
            return false;
          }

          // HIDE AND SNEAK FOR INBOX
          if (tab.key === 'tasks' && user?.permissions?.includes('hideLegacyInbox')) {
            return false;
          }

          //NULL-SAFE CHECK FOR EITHER PERMISSION
          if (tab.key === 'deals') {
            return (tab.permission) ? (user?.permissions?.includes('viewFMTeamDeals') || user?.permissions?.includes(tab.permission)) :
               (user?.permissions?.includes('viewFMTeamDeals'));
          }

          if (!tab.permission) {
              return true;
          }
          return user?.permissions?.includes(tab.permission);
      };
  }

}

